import React, { useState, useEffect } from 'react'
import { Button } from 'react-admin'
import { entries, filterObject, keys, mapValues } from 'services/utils'
import {
  getI18nBundleOperations,
  I18nMap,
  UpdateFileFunction,
} from './I18nFileProvider'
import I18nLabelsForm from './I18nLabelsForm'
import { I18nFile } from 'services/geo-management'
import { ClearbleInputFilter } from '../../ui-shared/CustomInputs/ClearbleInputFilter'
import {
  Table,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material'

const ROWS_PER_PAGE = 20
const PLACEHOLDER_STRING = 'TBD'
const MAIN_LANG_FILECODE: I18nFile = 'it-IT'
export type UpdateTranslationFn = (
  key: string,
  code: string
) => ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => void

export type DeleteLabelFn = (key: string) => void

type Ii18nTableProps = {
  apiI18nBundle: I18nMap
  saveFunction: UpdateFileFunction
  isSaving: boolean
  requiredFiles: I18nFile[]
}
export type TranslationFilter = {
  lang: I18nFile
  value: string
}
const I18nFormHandler: React.FC<Ii18nTableProps> = ({
  apiI18nBundle,
  saveFunction,
  isSaving,
  requiredFiles,
}) => {
  const [i18nMap, setI18nMap] = useState<I18nMap>(apiI18nBundle)
  const [newLabel, setNewLabel] = useState<string>('')
  const [keyFilterText, setKeyFilterText] = useState<string>('')
  const [translationFilter, setTranslationFilter] = useState<TranslationFilter>(
    { lang: MAIN_LANG_FILECODE, value: '' }
  )
  const [isDeleteEnabled, setIsDeleteEnabled] = useState<boolean>(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE)
  useEffect(() => {
    setI18nMap(apiI18nBundle)
  }, [apiI18nBundle])

  const jsonWidthDiffs = entries(i18nMap).map(
    ([code, json]) => !!getI18nBundleOperations(json, apiI18nBundle[code])
  )

  const disableSaveButton =
    isSaving ||
    jsonWidthDiffs.every((langsModifieds) => langsModifieds === false)

  const updateTranslation: UpdateTranslationFn =
    (key, code) =>
    ({ target: { value } }) => {
      if (!!translationFilter.value) {
        setKeyFilterText(key)
      }
      setTranslationFilter({ lang: code as I18nFile, value: '' })
      setI18nMap({ ...i18nMap, [code]: { ...i18nMap[code], [key]: value } })
    }
  const deleteLabel: DeleteLabelFn = (key) => {
    setI18nMap({
      ...mapValues(
        (v) => ({ ...filterObject((_v, name) => name !== key, v) }),
        i18nMap
      ),
    } as I18nMap)
    setIsDeleteEnabled(false)
  }
  const handlePageChange = (
    _e: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(Number(event.target.value))
    setPage(0)
  }

  // testing to render page only when change filter
  // const [filteredRows, setFilteredRows] = useState<string[]>(
  //   keys(i18nMap[MAIN_LANG_FILECODE])
  // )
  // useEffect(() => {
  //   setFilteredRows(
  //     keys(i18nMap[MAIN_LANG_FILECODE]).filterEvery([
  //       (k) => k.toLowerCase().includes(keyFilterText.toLowerCase()),
  //       (k) =>
  //         !!i18nMap[translationFilter.lang][k]
  //           ?.toLowerCase()
  //           .includes(translationFilter.value.toLowerCase()),
  //     ])
  //   )
  // }, [keyFilterText, translationFilter])

  const filteredRows = keys(i18nMap[MAIN_LANG_FILECODE]).filterEvery([
    (k) => k.toLowerCase().includes(keyFilterText.toLowerCase()),
    (k) =>
      !!i18nMap[translationFilter.lang][k]
        ?.toLowerCase()
        .includes(translationFilter.value.toLowerCase()),
  ])

  const labelsToRender = filteredRows.slice(
    rowsPerPage * page,
    rowsPerPage * page + rowsPerPage
  )
  // Used to set page 0 only when rowsPerPage or LabelsTorender.length change. IF not do this, the page will be set to 0 in other cases.
  useEffect(() => {
    if (labelsToRender.length < rowsPerPage) {
      setPage(0)
    }
  }, [rowsPerPage, labelsToRender])
  const addLabel = (key: string) => {
    setI18nMap({
      ...mapValues((v) => ({ [key]: PLACEHOLDER_STRING, ...v }), i18nMap),
    })
    setNewLabel('')
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ClearbleInputFilter
            value={newLabel}
            handleChange={(e) => setNewLabel(e.target.value)}
            label="New Label"
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && !!newLabel) {
                addLabel(newLabel)
              }
            }}
            handleClear={() => setNewLabel('')}
          />
          <Button
            label="Add label"
            variant="contained"
            disabled={!newLabel}
            onClick={() => addLabel(newLabel!)}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            onClick={saveFunction(i18nMap)}
            label="Save"
            variant="contained"
            disabled={disableSaveButton}
            style={{ marginRight: '16px' }}
          />
          <Button
            onClick={() => setIsDeleteEnabled(!isDeleteEnabled)}
            label={isDeleteEnabled ? 'Disable delete' : 'Enable delete'}
            variant="contained"
          />
        </div>
      </div>
      <TableContainer style={{ height: '78vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <ClearbleInputFilter
                value={keyFilterText}
                handleChange={(e) => setKeyFilterText(e.target.value)}
                label="Filter by key"
                handleClear={() => setKeyFilterText('')}
              />
              {requiredFiles.map((i18nFileCode, i) => (
                <ClearbleInputFilter
                  key={i}
                  value={
                    translationFilter.lang === i18nFileCode
                      ? translationFilter.value
                      : ''
                  }
                  handleChange={(e) =>
                    setTranslationFilter({
                      value: e.target.value,
                      lang: i18nFileCode,
                    })
                  }
                  label={`Filter by ${i18nFileCode}`}
                  handleClear={() =>
                    setTranslationFilter({ ...translationFilter, value: '' })
                  }
                />
              ))}
            </TableRow>
          </TableHead>

          <I18nLabelsForm
            labelsToRender={labelsToRender}
            i18nMap={i18nMap}
            updateTranslation={updateTranslation}
            deleteLabel={deleteLabel}
            isDeleteEnabled={isDeleteEnabled}
          />
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={[
          ROWS_PER_PAGE,
          ROWS_PER_PAGE * 2,
          ROWS_PER_PAGE * 3,
        ]}
        count={filteredRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        SelectProps={{
          inputProps: {
            'aria-label': 'rows per page',
          },
          native: true,
        }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  )
}

export default I18nFormHandler
